// src/App.tsx

import React, { useEffect, useState, useRef } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import "@/App.css";
import "@/styles/react-calendar.shadcn.css";
import "react-calendar/dist/Calendar.css";

import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import Dashboard from "./pages/Dashboard";
import EmployeeList from "./pages/admin/EmployeeList";
import AddEmployee from "./pages/admin/AddEmployee";
import EditEmployee from "./pages/admin/EditEmployee";
import RunPayroll from "./pages/admin/payroll/RunPayroll";
import SalaryStructure from "./pages/admin/SalaryStructure";
import Compliance from "./pages/Compliance";
import Payslips from "./pages/Payslips";
import Settings from "./pages/Settings";
import EmployeeLayout from "./components/employee/EmployeeLayout";
import EmployeeDashboard from "./pages/employee/EmployeeDashboard";
import EmployeeProfile from "./pages/employee/EmployeeProfile";
import EmployeePayslips from "./pages/employee/EmployeePayslips";
import EmployeeLeave from "./pages/employee/EmployeeLeave";
import EmployeeLoans from "./pages/employee/EmployeeLoans";
import EmployeeAttendance from "./pages/employee/EmployeeAttendance";
import EmployeeDocuments from "./pages/employee/EmployeeDocuments";
import EmployeeReviews from './pages/employee/EmployeeReviews';
import EmployeePIPView from './pages/employee/EmployeePIPView';
import EmployeeExitInterviewView from './pages/employee/EmployeeExitInterviewView';
import UserManual from "./pages/employee/UserManual";
import LeaveManagement from "./pages/admin/LeaveManagement";
import LoanManagement from "./pages/admin/LoanManagement";
import ReimbursementManagement from "./pages/admin/ReimbursementManagement";
import EmployeeImport from "./pages/admin/EmployeeImport";
import LoginDetails from "./pages/admin/LoginDetails";
import EventManagement from "./pages/admin/EventManagement";
import CompanyDocuments from "./pages/admin/CompanyDocuments";
import EmployeeCompanyDocuments from "./pages/employee/CompanyDocuments";
import SalaryComponents from "./pages/admin/SalaryComponents";
import TaxConfiguration from "./pages/admin/TaxConfiguration";

// Super Admin imports
import SuperAdminLayout from "./components/super-admin/SuperAdminLayout";
import SuperAdminDashboard from "./pages/super-admin/SuperAdminDashboard";
import CompaniesList from "./pages/super-admin/CompaniesList";
import AddCompany from "./pages/super-admin/AddCompany";
import CompanyDetails from "./pages/super-admin/CompanyDetails";
import EditCompany from "./pages/super-admin/EditCompany";
import SubscriptionPlans from "./pages/super-admin/SubscriptionPlans";
import EditPlan from "./pages/super-admin/EditPlan";
import PlatformPayments from "./pages/super-admin/PlatformPayments";
import AppVersions from "./pages/super-admin/AppVersions";
import SendNotification from "./pages/super-admin/SendNotification";
import AppUpdateGate from "./components/common/AppUpdateGate";

import AcceptInvitation from "./pages/AcceptInvitation";
import UpgradeRequired from "./pages/UpgradeRequired";
import UpgradePlan from "./pages/UpgradePlan";
import FeatureProtectedRoute from "./components/FeatureProtectedRoute";
import PayrollAnalytics from "./pages/PayrollAnalytics";
import AuditLogs from "./pages/AuditLogs";

// Public pages
import PricingPage from "./pages/auth/PricingPage";
import SignupPage from "./pages/auth/SignupPage";
import LeaveConfiguration from "./pages/admin/LeaveConfiguration";

// Attendance Pages
import AttendanceDashboard from "./pages/admin/AttendanceDashboard";
import Regularization from "./pages/admin/Regularization";
import Reports from "./pages/admin/Reports";
import Shifts from "./pages/admin/Shifts";
import ViewEmployeeDetails from "./pages/admin/ViewEmployeeDetails";

import { OnboardingProvider } from "./contexts/OnboardingContext";
import NavigationEvents from "./components/NavigationEvents";
import PrivacyPolicy from "./components/common/PrivacyPolicy";
import AcceptEmployeeInvitation from "./pages/AcceptEmployeeInvitation";
import ZenithLanding from "./pages/ZenithLanding";
import { useAndroidBackButton } from "./hooks/useAndroidBackButton";
import { useStartupLocationPermission } from "./hooks/useStartupLocationPermission";
import ContactUs from "./pages/ContactUs";
import { RoleAccessProvider } from "./contexts/RoleAccessContext";
import AccessControlPage from "./pages/admin/AccessControl";
import SessionExpiredModal from "./components/ui/SessionExpiredModal";
import AdminProfile from "./pages/admin/Profile";
import PageNotFound from "./pages/PageNotFound";
import NotificationsPage from "./components/common/NotificationsPage";
import {
  getAdminNotificationRoute,
  getEmployeePortalNotificationRoute,
  getLinkedAdminNotificationRoute,
  getSuperAdminNotificationRoute,
} from "./lib/notifications";
import { isLinkedAdmin, isEmployeeWorkspacePath } from "./utils/workspace";
import { TeamProvider, useTeam } from "./contexts/TeamContext";
import TeamLeave from "./pages/team/TeamLeave";
import {
  TeamReviews,
  TeamAppraisal,
  TeamPIP,
  TeamExitInterview,
  TeamGoals,
} from "./pages/team/TeamPages";
import AppraisalBuilder from "./pages/admin/performance/AppraisalBuilder";

// Performance Management Imports
import PerformanceDashboard from "./pages/admin/performance/Dashboard";
import GoalsPage from "./pages/admin/performance/GoalsPage";
import MyAppraisalsPage from "./pages/admin/performance/MyAppraisalsPage";
import TeamReviewPage from "./pages/admin/performance/TeamReviewPage";
import AppraisalEditor from "./pages/admin/performance/AppraisalEditor";
import ExitInterviewForm from "./pages/admin/performance/ExitInterviewForm";
import PIPForm from "./pages/admin/performance/PIPForm";

import AppraisalEditorRouter from "./pages/admin/performance/AppraisalEditorRouter";
import EmployeeGoalView from "./pages/employee/EmployeeGoalView";
import ScrollToTop from "./components/common/ScrollToTop";
import OfflineFallback from "./components/common/OfflineFallback";

// Help Center (public, standalone) — lazy-loaded so the help content stays
// out of the main bundle
const HelpCenter = React.lazy(() => import("./pages/help/HelpCenter"));
const HelpArticle = React.lazy(() => import("./pages/help/HelpArticle"));
const HelpFallback = () => <div className="min-h-screen bg-background" />;

// The employee's own pages (paths relative to /employee). Employees get them in
// the employee portal; an admin who is also an employee gets the same pages
// inside the admin layout ("My workspace" in the sidebar).
const EMPLOYEE_PAGES = [
  {
    path: "dashboard",
    element: (
      <FeatureProtectedRoute>
        <EmployeeDashboard />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "profile",
    element: (
      <FeatureProtectedRoute>
        <EmployeeProfile />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "payslips",
    element: (
      <FeatureProtectedRoute>
        <EmployeePayslips />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "leave",
    element: (
      <FeatureProtectedRoute
        feature="basic_leave_management"
        featureOr="custom_leave_management"
      >
        <EmployeeLeave />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "finances",
    element: (
      <FeatureProtectedRoute>
        <EmployeeLoans />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "attendance",
    element: (
      <FeatureProtectedRoute>
        <EmployeeAttendance />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "documents",
    element: (
      <FeatureProtectedRoute feature="document_upload">
        <EmployeeDocuments />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "company-documents",
    element: (
      <FeatureProtectedRoute>
        <EmployeeCompanyDocuments />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "user-manual",
    element: (
      <FeatureProtectedRoute>
        <UserManual />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "reviews",
    element: (
      <FeatureProtectedRoute feature="performance_management">
        <EmployeeReviews />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "review/:id",
    element: (
      <FeatureProtectedRoute feature="performance_management">
        <AppraisalEditorRouter />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "pip/:id",
    element: (
      <FeatureProtectedRoute feature="performance_management">
        <EmployeePIPView />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "exit-interview/:id",
    element: (
      <FeatureProtectedRoute feature="performance_management">
        <EmployeeExitInterviewView />
      </FeatureProtectedRoute>
    ),
  },
  {
    path: "goal/:id",
    element: (
      <FeatureProtectedRoute feature="performance_management">
        <EmployeeGoalView />
      </FeatureProtectedRoute>
    ),
  },
];

const AdminRoute = ({ permission, children }) => {
  return (
    <ProtectedRoute requireRole="admin" permission={permission}>
      {children}
    </ProtectedRoute>
  );
};

// "My team" pages for anyone who manages people, guarded by the company's
// manager duties. `duties` lists the duties that open a page (any one is
// enough).
const TeamRoute = ({ duties, children }) => {
  const team = useTeam();
  const { user } = useAuth();
  if (team.loading && !team.isManager) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }
  const allowed = team.isManager && duties.some((duty) => team.duties[duty]);
  if (allowed) return children;
  return <Navigate to={user?.role === "employee" ? "/employee/dashboard" : "/dashboard"} replace />;
};

// Team page routes, relative to `basePath` (/team or /employee/team).
const teamPages = (basePath) => [
  {
    path: "leave",
    element: (
      <TeamRoute duties={["leave"]}>
        <FeatureProtectedRoute feature="basic_leave_management" featureOr="custom_leave_management">
          <TeamLeave />
        </FeatureProtectedRoute>
      </TeamRoute>
    ),
  },
  {
    path: "reviews",
    element: (
      <TeamRoute duties={["appraisals", "pips"]}>
        <FeatureProtectedRoute feature="performance_management">
          <TeamReviews basePath={basePath} />
        </FeatureProtectedRoute>
      </TeamRoute>
    ),
  },
  {
    path: "review/:id",
    element: (
      <TeamRoute duties={["appraisals"]}>
        <FeatureProtectedRoute feature="performance_management">
          <TeamAppraisal basePath={basePath} />
        </FeatureProtectedRoute>
      </TeamRoute>
    ),
  },
  {
    path: "pip/:id",
    element: (
      <TeamRoute duties={["pips"]}>
        <FeatureProtectedRoute feature="performance_management">
          <TeamPIP basePath={basePath} />
        </FeatureProtectedRoute>
      </TeamRoute>
    ),
  },
  {
    path: "exit-interview/:id",
    element: (
      <TeamRoute duties={["appraisals"]}>
        <FeatureProtectedRoute feature="performance_management">
          <TeamExitInterview basePath={basePath} />
        </FeatureProtectedRoute>
      </TeamRoute>
    ),
  },
  {
    path: "goals",
    element: (
      <TeamRoute duties={["goals"]}>
        <FeatureProtectedRoute feature="performance_management">
          <TeamGoals basePath={basePath} />
        </FeatureProtectedRoute>
      </TeamRoute>
    ),
  },
];

const ADMIN_TEAM_PAGES = teamPages("/team");
const EMPLOYEE_TEAM_PAGES = teamPages("/employee/team");

const adminRouteMatchers = [
  /^\/dashboard$/,
  /^\/team\/(leave|reviews|goals)$/,
  /^\/team\/(review|pip|exit-interview)\/[^/]+$/,
  /^\/upgrade$/,
  /^\/employees$/,
  /^\/employees\/view$/,
  /^\/employees\/new$/,
  /^\/employees\/import$/,
  /^\/employees\/[^/]+\/edit$/,
  /^\/payroll\/run$/,
  /^\/payroll\/salary-structure$/,
  /^\/compliance$/,
  /^\/payslips$/,
  /^\/leave-management$/,
  /^\/loan-management$/,
  /^\/reimbursement-management$/,
  /^\/admin\/login-details$/,
  /^\/admin\/events$/,
  /^\/company-documents$/,
  /^\/admin\/profile$/,
  /^\/notifications$/,
  /^\/attendance\/dashboard$/,
  /^\/attendance\/regularization$/,
  /^\/attendance\/reports$/,
  /^\/attendance\/shifts$/,
  /^\/settings$/,
  /^\/salary-components$/,
  /^\/tax-configuration$/,
  /^\/leave-configuration$/,
  /^\/payroll-analytics$/,
  /^\/activity-logs$/,
  /^\/permission$/,
  /^\/template-builder$/,
  /^\/performance$/,
  /^\/performance\/goals$/,
  /^\/performance\/reviews$/,
  /^\/performance\/my-appraisals$/,
  /^\/performance\/review\/new$/,
  /^\/performance\/review\/[^/]+$/,
  /^\/performance\/exit-interview\/[^/]+$/,
  /^\/performance\/pip\/new$/,
  /^\/performance\/pip\/[^/]+$/,
  /^\/performance\/employee\/[^/]+$/,
];

const normalizePathname = (pathname = "") => {
  if (!pathname) return "/";
  if (pathname !== "/" && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }
  return pathname;
};

const isKnownAdminRoute = (pathname) => {
  const normalizedPath = normalizePathname(pathname);
  return adminRouteMatchers.some((re) => re.test(normalizedPath));
};

function AdminAppRoute() {
  const location = useLocation();
  const { isAuthenticated, user } = useAuth();
  const isWorkspacePage =
    isLinkedAdmin(user) && isEmployeeWorkspacePath(normalizePathname(location.pathname));

  if (!isKnownAdminRoute(location.pathname) && !isWorkspacePage) {
    if (isAuthenticated && user?.role === "employee") {
      return (
        <Navigate
          to="/employee/not-found"
          state={{ from: location.pathname }}
          replace
        />
      );
    }

    return (
      <ProtectedRoute requireRole="admin">
        <PageNotFound />
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute requireRole="admin">
      <AdminLayout />
    </ProtectedRoute>
  );
}

// The employee's own pages, for an admin who is also an employee.
const LinkedAdminRoute = ({ children }) => {
  const { user } = useAuth();
  return isLinkedAdmin(user) ? children : <Navigate to="/dashboard" replace />;
};

// Employees' own items, and — for those who manage people — their team's.
const EmployeeNotificationsPage = () => {
  const { user } = useAuth();
  return <NotificationsPage resolveRoute={getEmployeePortalNotificationRoute(user?.employee_id)} />;
};

const AdminNotificationsPage = () => {
  const { user } = useAuth();
  return (
    <NotificationsPage
      resolveRoute={
        isLinkedAdmin(user)
          ? getLinkedAdminNotificationRoute(user.employee_id)
          : getAdminNotificationRoute
      }
    />
  );
};

// Admin Layout Component
const AdminLayout = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Phones: the navigation is a drawer opened from the top bar's menu button.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="flex h-screen-safe bg-background dark:bg-background">
      {/* Sidebar */}
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />

      {/* Main Content.
          From md up the column clears the sidebar (76px rail or 264px panel).
          Below md there is no rail — the sidebar is an off-canvas drawer — so
          the page gets the full width of the phone. */}
      <div
        className={`flex-1 min-w-0 flex flex-col transition-all duration-300 ml-0 ${sidebarCollapsed ? "md:ml-[76px]" : "md:ml-[264px]"
          }`}
      >
        {/* Top Bar */}
        <TopBar onOpenNav={() => setMobileNavOpen(true)} />

        {/* Page Content */}
        <main className="flex-1 min-w-0 overflow-y-auto p-4 md:p-6 bg-background">
          <Routes>
            <Route
              path="/dashboard"
              element={
                <AdminRoute>
                  <Dashboard />
                </AdminRoute>
              }
            />

            <Route
              path="/upgrade"
              element={
                <AdminRoute
                  permission={{
                    feature: "subscription_management",
                    actions: ["edit"],
                  }}
                >
                  <UpgradePlan />
                </AdminRoute>
              }
            />

            {/* ---------------- Employees ---------------- */}
            <Route
              path="/employees/view"
              element={
                <AdminRoute
                  permission={{
                    feature: "employees",
                    actions: ["view"],
                  }}
                >
                  <ViewEmployeeDetails />
                </AdminRoute>
              }
            />

            <Route
              path="/employees"
              element={
                <AdminRoute
                  permission={{
                    feature: "employees",
                    actions: ["view"],
                  }}
                >
                  <EmployeeList />
                </AdminRoute>
              }
            />

            <Route
              path="/employees/new"
              element={
                <AdminRoute
                  permission={{
                    feature: "employees",
                    actions: ["create"],
                  }}
                >
                  <AddEmployee />
                </AdminRoute>
              }
            />

            <Route
              path="/employees/import"
              element={
                <AdminRoute
                  permission={{
                    feature: "employees",
                    actions: ["create"],
                  }}
                >
                  <FeatureProtectedRoute feature="bulk_import">
                    <EmployeeImport />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/employees/:id/edit"
              element={
                <AdminRoute
                  permission={{
                    feature: "employees",
                    actions: ["edit"],
                  }}
                >
                  <EditEmployee />
                </AdminRoute>
              }
            />

            {/* ---------------- Payroll ---------------- */}
            <Route
              path="/payroll/run"
              element={
                <AdminRoute
                  permission={{
                    feature: "run_payroll",
                    actions: ["view"],
                  }}
                >
                  <RunPayroll />
                </AdminRoute>
              }
            />

            <Route
              path="/payroll/salary-structure"
              element={
                <AdminRoute
                  permission={{
                    feature: "salary_structure",
                    actions: ["view"],
                  }}
                >
                  <SalaryStructure />
                </AdminRoute>
              }
            />

            {/* ---------------- Compliance ---------------- */}
            <Route
              path="/compliance"
              element={
                <AdminRoute>
                  <Compliance />
                </AdminRoute>
              }
            />

            {/* ---------------- Payslips ---------------- */}
            <Route
              path="/payslips"
              element={
                <AdminRoute
                  permission={{
                    feature: "payslips",
                    actions: ["view"],
                  }}
                >
                  <Payslips />
                </AdminRoute>
              }
            />

            {/* ---------------- Leave Management ---------------- */}
            <Route
              path="/leave-management"
              element={
                <AdminRoute
                  permission={{
                    feature: "leave_management",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute
                    feature="basic_leave_management"
                    featureOr="custom_leave_management"
                  >
                    <LeaveManagement />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            {/* ---------------- Loan Management ---------------- */}
            <Route
              path="/loan-management"
              element={
                <AdminRoute
                  permission={{
                    feature: "loan_management",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="loan_management">
                    <LoanManagement />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            {/* ---------------- Reimbursement Management ---------------- */}
            <Route
              path="/reimbursement-management"
              element={
                <AdminRoute
                  permission={{
                    feature: "reimbursement_management",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="advanced_payroll">
                    <ReimbursementManagement />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            {/* ---------------- Login Details ---------------- */}
            <Route
              path="/admin/login-details"
              element={
                <AdminRoute
                  permission={{
                    feature: "login_details",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="employee_portal">
                    <LoginDetails />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            {/* ---------------- Event Management ---------------- */}
            <Route
              path="/admin/events"
              element={
                <AdminRoute
                  permission={{
                    feature: "event_management",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="event_management">
                    <EventManagement />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            {/* ---------------- Company Documents (all plans) ---------------- */}
            <Route
              path="/company-documents"
              element={
                <AdminRoute
                  permission={{
                    feature: "company_documents",
                    actions: ["view"],
                  }}
                >
                  <CompanyDocuments />
                </AdminRoute>
              }
            />

            <Route
              path="/admin/profile"
              element={
                <AdminProfile />
              }
            />

            {/* Notifications inbox — every admin, no permission needed */}
            <Route
              path="/notifications"
              element={
                <AdminRoute>
                  <AdminNotificationsPage />
                </AdminRoute>
              }
            />

            {/* ---------------- My workspace (admins who are also employees) ---------------- */}
            {EMPLOYEE_PAGES.map(({ path, element }) => (
              <Route
                key={path}
                path={`/employee/${path}`}
                element={<LinkedAdminRoute>{element}</LinkedAdminRoute>}
              />
            ))}
            {/* ---------------- My team (anyone who manages people) ---------------- */}
            {ADMIN_TEAM_PAGES.map(({ path, element }) => (
              <Route key={`team-${path}`} path={`/team/${path}`} element={element} />
            ))}

            {/* One notifications feed covers both identities. */}
            <Route path="/employee/notifications" element={<Navigate to="/notifications" replace />} />
            <Route path="/employee/*" element={<Navigate to="/employee/dashboard" replace />} />

            {/* ---------------- Attendance ---------------- */}
            <Route
              path="/attendance/dashboard"
              element={
                <AdminRoute
                  permission={{
                    feature: "attendance_dashboard",
                    actions: ["view"],
                  }}
                >
                  <AttendanceDashboard />
                </AdminRoute>
              }
            />

            <Route
              path="/attendance/regularization"
              element={
                <AdminRoute
                  permission={{
                    feature: "regularization_requests",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="regularization_requests">
                    <Regularization />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/attendance/reports"
              element={
                <AdminRoute
                  permission={{
                    feature: "attendance_reports",
                    actions: ["view"],
                  }}
                >
                  <Reports />
                </AdminRoute>
              }
            />

            <Route
              path="/attendance/shifts"
              element={
                <AdminRoute
                  permission={{
                    feature: [
                      "shifts",
                      "overtime_management",
                      "rotational_shift_schedules",
                    ],
                    actions: ["view"],
                  }}
                >
                  <Shifts />
                </AdminRoute>
              }
            />

            {/* ---------------- Settings ---------------- */}
            <Route
              path="/settings"
              element={
                <AdminRoute
                  permission={{
                    feature: [
                      "settings_company_configuration",
                      "attendance_and_policy_configuration",
                      "settings_notification_configuration",
                      "settings_security_configuration",
                      "settings_bank_configuration",
                      "settings_loan_configuration",
                      "settings_leave_configuration",
                      "work_locations_configuration",
                      "department_configuration",
                      "designation_configuration",
                    ],
                    actions: ["view"],
                  }}
                >
                  <Settings />
                </AdminRoute>
              }
            />

            <Route
              path="/salary-components"
              element={
                <AdminRoute
                  permission={{
                    feature: "salary_components_configuration",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="advanced_payroll">
                    <SalaryComponents />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/tax-configuration"
              element={
                <AdminRoute
                  permission={{
                    feature: "tax_configuration",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="advanced_payroll">
                    <TaxConfiguration />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/leave-configuration"
              element={
                <AdminRoute
                  permission={{
                    feature: ["settings_leave_configuration", "holiday_configuration"],
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute
                    feature="basic_leave_management"
                    featureOr="custom_leave_management"
                  >
                    <LeaveConfiguration />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/template-builder"
              element={
                <AdminRoute
                  permission={{
                    feature:"template_builder",
                    actions:["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="performance_management">
                    <AppraisalBuilder />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            {/* ---------------- Performance Management ---------------- */}
            <Route
              path="/performance"
              element={
                <AdminRoute
                  permission={{
                    feature:"performance_management",
                    actions:["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="performance_management">
                    <PerformanceDashboard />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/performance/goals"
              element={
                <AdminRoute
                  permission={{
                    feature:"goals_okrs",
                    actions:["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="performance_management">
                    <GoalsPage />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/performance/my-appraisals"
              element={
                <AdminRoute
                  permission={{
                    feature:"performance_management",
                    actions:["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="performance_management">
                    <MyAppraisalsPage />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/performance/review/new"
              element={
                <AdminRoute
                  permission={{
                    feature:"reviews",
                    actions:["view", "create"],
                  }}
                >
                  <FeatureProtectedRoute feature="performance_management">
                    <AppraisalEditorRouter />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/performance/review/:id"
              element={
                <AdminRoute
                  permission={{
                    feature:"performance_management",
                    actions:["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="performance_management">
                    <AppraisalEditorRouter />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/performance/exit-interview/:id"
              element={
                <AdminRoute
                  permission={{
                    feature: "performance_management",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="performance_management">
                    <ExitInterviewForm />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/performance/pip/new"
              element={
                <AdminRoute
                  permission={{
                    feature: "performance_management",
                    actions: ["view", "create"],
                  }}
                >
                  <FeatureProtectedRoute feature="performance_management">
                    <PIPForm />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/performance/pip/:id"
              element={
                <AdminRoute
                  permission={{
                    feature:"performance_management",
                    actions:["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="performance_management">
                    <PIPForm />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            {/* ---------------- Analytics & Logs ---------------- */}
            <Route
              path="/payroll-analytics"
              element={
                <AdminRoute
                  permission={{
                    feature: "payroll_analytics",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="payroll_analytics">
                    <PayrollAnalytics />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route
              path="/activity-logs"
              element={
                <AdminRoute
                  permission={{
                    feature: "activity_logs",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="activity_logs">
                    <AuditLogs />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            {/* ---------------- Access Control ---------------- */}
            <Route
              path="/permission"
              element={
                <AdminRoute
                  permission={{
                    feature: "roles_and_access",
                    actions: ["view"],
                  }}
                >
                  <FeatureProtectedRoute feature="roles_and_access">
                    <AccessControlPage />
                  </FeatureProtectedRoute>
                </AdminRoute>
              }
            />

            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};






function BackHandler() {
  useAndroidBackButton();
  return null;
}

function RouterShell({ authExpiredOpen, setAuthExpiredOpen }) {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleOk = async () => {
    setAuthExpiredOpen(false);
    await logout();
    navigate("/");
  };

  return (
    <>
      <SessionExpiredModal
        open={authExpiredOpen}
        onOk={handleOk}
        onOpenChange={setAuthExpiredOpen}
      />
    </>
  );
}

// Top-level routes. Inside AuthProvider so they can depend on who's signed in:
// an admin who is also an employee gets the employee pages inside the admin
// layout, so the employee portal's own route is left out for them.
function AppRoutes({ networkGeneration }) {
  const { user } = useAuth();
  const linkedAdmin = isLinkedAdmin(user);

  return (
    <Routes key={networkGeneration}>
      {/* Public Routes */}
      <Route path="/" element={<ZenithLanding />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/policy" element={<PrivacyPolicy />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route
        path="/help"
        element={
          <React.Suspense fallback={<HelpFallback />}>
            <HelpCenter />
          </React.Suspense>
        }
      />
      <Route
        path="/help/:slug"
        element={
          <React.Suspense fallback={<HelpFallback />}>
            <HelpArticle />
          </React.Suspense>
        }
      />
      <Route
        path="/accept-invitation"
        element={<AcceptInvitation />}
      />
      <Route
        path="/accept-employee-invite"
        element={<AcceptEmployeeInvitation />}
      />
      <Route
        path="/accept-admin-access"
        element={<AcceptEmployeeInvitation />}
      />

      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            {/* Standalone route has no AdminLayout, so it mirrors
                AdminLayout's <main> exactly (same padding, bg and
                scroll viewport) — so the page's spacing matches
                the rest of the app and isn't clipped at the bottom
                in the Capacitor/WKWebView shell. */}
            <div className="h-screen-safe overflow-y-auto p-6 bg-background">
              <UpgradePlan />
            </div>
          </ProtectedRoute>
        }
      />

      {/* Protected Super Admin Routes */}
      <Route
        path="/super-admin/*"
        element={
          <ProtectedRoute requireRole="super_admin">
            <SuperAdminLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<SuperAdminDashboard />} />
        <Route path="companies" element={<CompaniesList />} />
        <Route path="companies/add" element={<AddCompany />} />
        <Route path="companies/details" element={<CompanyDetails />} />
        <Route path="companies/edit" element={<EditCompany />} />
        <Route path="plans" element={<SubscriptionPlans />} />
        <Route path="plans/:planId/edit" element={<EditPlan />} />
        <Route path="payments" element={<PlatformPayments />} />
        <Route path="app-versions" element={<AppVersions />} />
        <Route path="notifications" element={<SendNotification />} />
        {/* The super admin's own inbox ("notifications" is the broadcast sender) */}
        <Route
          path="inbox"
          element={<NotificationsPage resolveRoute={getSuperAdminNotificationRoute} />}
        />
        <Route
          index
          element={<Navigate to="/super-admin/dashboard" replace />}
        />
        <Route path="*" element={<PageNotFound />} />
      </Route>

      {/* Protected Admin Routes */}
      <Route
        path="/*"
        element={<AdminAppRoute />}
      />

      {/* Protected Employee Routes. A linked admin (also an
          employee of the company) gets these pages inside the
          admin layout instead — see AdminLayout. */}
      {!linkedAdmin && (
        <Route
          path="/employee/*"
          element={
            <ProtectedRoute requireRole="employee">
              <EmployeeLayout />
            </ProtectedRoute>
          }
        >
          <Route
            path="notifications"
            element={<EmployeeNotificationsPage />}
          />
          {EMPLOYEE_PAGES.map(({ path, element }) => (
            <Route key={path} path={path} element={element} />
          ))}
          {EMPLOYEE_TEAM_PAGES.map(({ path, element }) => (
            <Route key={`team-${path}`} path={`team/${path}`} element={element} />
          ))}
          <Route
            index
            element={<Navigate to="/employee/dashboard" replace />}
          />
          <Route
            path="*"
            element={<Navigate to="/employee/not-found" replace />}
          />
        </Route>
      )}

    {/* Default redirect */}
    <Route
      path="*"
      element={<PageNotFound />}
    />
  </Routes>
  );
}

function App() {
  useStartupLocationPermission();
  const [authExpiredOpen, setAuthExpiredOpen] = useState(false);

  // Bumped on every offline -> online transition. Used as a `key` on the
  // top-level <Routes> so the whole routed tree (pages + layouts + their
  // ProtectedRoute checks) remounts and re-runs its mount-time data fetches.
  // Requests fired while offline hang forever (axios timeout is 0) and leave
  // pages stuck on loading skeletons; nothing else refetches on reconnect, so
  // remounting is what lets the UI recover to fresh data with no manual action.
  const [networkGeneration, setNetworkGeneration] = useState(0);
  const wasOfflineRef = useRef(
    typeof navigator !== "undefined" && navigator.onLine === false
  );

  useEffect(() => {
    const handleAuthExpired = () => {
      setAuthExpiredOpen(true);
      // navigate("/");
    };

    const handleOffline = () => {
      wasOfflineRef.current = true;
    };

    const handleOnline = () => {
      // Only recover if we were actually offline (ignore spurious events).
      if (!wasOfflineRef.current) return;
      wasOfflineRef.current = false;
      // Small delay so the connection is usable before we refetch — the
      // browser fires `online` as soon as an interface is up, which can be a
      // hair before requests actually succeed.
      window.setTimeout(() => {
        setNetworkGeneration((gen) => gen + 1);
      }, 400);
    };

    window.addEventListener("auth-expired", handleAuthExpired);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("auth-expired", handleAuthExpired);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <RoleAccessProvider>
              <TeamProvider>
              <OnboardingProvider>
                <WebSocketProvider>
                  <BrowserRouter>
                  <ScrollToTop/>
                    <RouterShell
                      authExpiredOpen={authExpiredOpen}
                      setAuthExpiredOpen={setAuthExpiredOpen}
                    />
                    <BackHandler />
                    <NavigationEvents />
                    <AppUpdateGate />

                    <AppRoutes networkGeneration={networkGeneration} />

                    {/* Toast notifications */}
                    <Toaster
                      position="top-right"
                      richColors
                      expand={true}
                      visibleToasts={5}
                      toastOptions={{
                        className: "mt-8 sm:mt-0",
                      }}
                    />

                    {/* Offline fallback — blocks the app when the device
                        loses internet so no failed requests / error toasts
                        can be triggered while disconnected. */}
                    <OfflineFallback />
                  </BrowserRouter>
                </WebSocketProvider>
              </OnboardingProvider>
              </TeamProvider>
            </RoleAccessProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
